// ============================================
// ENUMS
// ============================================

export enum UserRole {
  PROVIDER = "provider",
  CUSTOMER = "customer",
  ADMIN = "admin",
}

export enum CareType {
  PRENATAL = "PRENATAL",
  POSTNATAL = "POSTNATAL",
  BOTH = "BOTH",
}

export enum ProviderStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  SUSPENDED = "SUSPENDED",
}

export enum VerificationStatus {
  PENDING = "PENDING",
  VERIFIED = "VERIFIED",
  REJECTED = "REJECTED",
}

export enum CentreStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  SUSPENDED = "SUSPENDED",
}

export enum StaffRole {
  OWNER = "OWNER",
  MANAGER = "MANAGER",
  RECEPTION = "RECEPTION",
  CARE_STAFF = "CARE_STAFF",
  COOK = "COOK",
}

export enum BookingStatus {
  PENDING = "PENDING",
  CONFIRMED = "CONFIRMED",
  ACTIVE = "ACTIVE",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
  REJECTED = "REJECTED",
}

export enum PaymentStatus {
  PENDING = "PENDING",
  PAID = "PAID",
  FAILED = "FAILED",
  REFUNDED = "REFUNDED",
  PARTIALLY_REFUNDED = "PARTIALLY_REFUNDED",
}

export enum RoomCategory {
  STANDARD = "STANDARD",
  DELUXE = "DELUXE",
  PREMIUM = "PREMIUM",
  SUITE = "SUITE",
  LUXURY = "LUXURY",
  VIP = "VIP",
  SHARED = "SHARED",
  PRIVATE = "PRIVATE",
  FAMILY_SUITE = "FAMILY_SUITE",
}

export const ROOM_CATEGORY_LABELS: Record<RoomCategory, string> = {
  [RoomCategory.STANDARD]: "Standard",
  [RoomCategory.DELUXE]: "Deluxe",
  [RoomCategory.PREMIUM]: "Premium",
  [RoomCategory.SUITE]: "Suite",
  [RoomCategory.LUXURY]: "Luxury",
  [RoomCategory.VIP]: "VIP",
  [RoomCategory.SHARED]: "Shared",
  [RoomCategory.PRIVATE]: "Private",
  [RoomCategory.FAMILY_SUITE]: "Family Suite",
};

export enum RoomLayoutType {
  STUDIO = "STUDIO",
  ONE_RK = "ONE_RK",
  ONE_BHK = "ONE_BHK",
  TWO_BHK = "TWO_BHK",
  THREE_BHK = "THREE_BHK",
  DORMITORY = "DORMITORY",
}

export const ROOM_LAYOUT_LABELS: Record<RoomLayoutType, string> = {
  [RoomLayoutType.STUDIO]: "Studio",
  [RoomLayoutType.ONE_RK]: "1 RK",
  [RoomLayoutType.ONE_BHK]: "1 BHK",
  [RoomLayoutType.TWO_BHK]: "2 BHK",
  [RoomLayoutType.THREE_BHK]: "3 BHK",
  [RoomLayoutType.DORMITORY]: "Dormitory",
};

export enum RoomStyle {
  PRIVATE = "PRIVATE",
  SHARED = "SHARED",
  FAMILY = "FAMILY",
  APARTMENT = "APARTMENT",
  VILLA = "VILLA",
  COTTAGE = "COTTAGE",
}

export const ROOM_STYLE_LABELS: Record<RoomStyle, string> = {
  [RoomStyle.PRIVATE]: "Private",
  [RoomStyle.SHARED]: "Shared",
  [RoomStyle.FAMILY]: "Family",
  [RoomStyle.APARTMENT]: "Apartment",
  [RoomStyle.VILLA]: "Villa",
  [RoomStyle.COTTAGE]: "Cottage",
};

export enum PaymentType {
  FULL = "FULL",
  ADVANCE = "ADVANCE",
}

export enum SubscriptionStatus {
  ACTIVE = "ACTIVE",
  EXPIRED = "EXPIRED",
  CANCELLED = "CANCELLED",
  SUSPENDED = "SUSPENDED",
  GRACE_PERIOD = "GRACE_PERIOD",
}

export enum ReviewStatus {
  PUBLISHED = "PUBLISHED",
  FLAGGED = "FLAGGED",
  REMOVED = "REMOVED",
}

export enum SenderType {
  CUSTOMER = "CUSTOMER",
  PROVIDER = "PROVIDER",
  SYSTEM = "SYSTEM",
}

export enum NotificationChannel {
  PUSH = "PUSH",
  SMS = "SMS",
  BOTH = "BOTH",
}

export enum AdminRole {
  SUPER_ADMIN = "SUPER_ADMIN",
  OPERATIONS = "OPERATIONS",
  SUPPORT = "SUPPORT",
  FINANCE = "FINANCE",
}

export enum DocType {
  TRADE_LICENCE = "TRADE_LICENCE",
  GST_CERTIFICATE = "GST_CERTIFICATE",
  CENTRE_PHOTO = "CENTRE_PHOTO",
}

export enum MedicalDocumentType {
  LICENSE = "LICENSE",
  INSURANCE = "INSURANCE",
  HEALTHCARE_REGISTRATION = "HEALTHCARE_REGISTRATION",
}

export enum BusinessDocumentType {
  BUSINESS_REGISTRATION = "BUSINESS_REGISTRATION",
  TAX_DOCUMENT = "TAX_DOCUMENT",
  PERMIT = "PERMIT",
}

export enum Language {
  MALAYALAM = "MALAYALAM",
  ENGLISH = "ENGLISH",
}

// ============================================
// INTERFACES
// ============================================

export interface Customer {
  id: string;
  phone: string;
  name?: string;
  careType?: CareType;
  dueDate?: Date;
  language: Language;
  city?: string;
  fcmToken?: string;
  isBlocked: boolean;
  createdAt: Date;
  lastActiveAt?: Date;
}

export interface Provider {
  id: string;
  ownerName: string;
  businessName: string;
  logoUrl?: string;
  phone: string;
  email?: string;
  whatsapp?: string;
  status: ProviderStatus;
  bankAccount?: any;
  upiId?: string;
  fcmToken?: string;
  rejectionReason?: string;
  approvedAt?: Date;
  approvedBy?: string;
  createdAt: Date;
}

export interface ProviderDocument {
  id: string;
  providerId: string;
  docType: DocType;
  fileUrl: string;
  uploadedAt: Date;
  verified: boolean;
}

export interface ProviderStaff {
  id: string;
  providerId: string;
  centreId?: string;
  name: string;
  phone: string;
  role: StaffRole;
  permissions?: any;
  appAccess: boolean;
  isActive: boolean;
  createdAt: Date;
}

export interface Centre {
  id: string;
  providerId: string;
  name: string;
  logoUrl?: string;
  description?: string;
  address: string;
  city: string;
  district?: string;
  state?: string;
  pincode?: string;
  lat?: number;
  lng?: number;
  phone?: string;
  whatsapp?: string;
  email?: string;
  careTypes: string[];
  totalBeds?: number;
  operatingHours?: any;
  staffLanguages: string[];
  status: CentreStatus;
  isVisible: boolean;
  showBranchesInCustomerApp?: boolean;
  avgRating?: number;
  reviewCount?: number;
  rejectionReason?: string;
  approvedAt?: Date;
  approvedBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface BusinessBranch {
  id: string;
  businessId: string;
  name?: string | null;
  isPrimary: boolean;
  address: string;
  city: string;
  district?: string | null;
  state?: string | null;
  pincode?: string | null;
  lat?: number | null;
  lng?: number | null;
  phone?: string | null;
  email?: string | null;
  whatsapp?: string | null;
  operatingHours?: Record<string, any> | null;
  status: "ACTIVE" | "INACTIVE";
  isVisibleInCustomerApp: boolean;
  managerStaffId?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export enum AmenityCategory {
  ROOM_FACILITIES = "ROOM_FACILITIES",
  MEDICAL_CARE = "MEDICAL_CARE",
  WELLNESS_RECOVERY = "WELLNESS_RECOVERY",
  FOOD_NUTRITION = "FOOD_NUTRITION",
  GENERAL_SERVICES = "GENERAL_SERVICES",
  OTHER = "OTHER",
}

export const AMENITY_CATEGORY_LABELS: Record<
  AmenityCategory,
  { en: string; ml: string }
> = {
  [AmenityCategory.ROOM_FACILITIES]: {
    en: "Room & Accommodation",
    ml: "മുറി സൗകര്യങ്ങൾ",
  },
  [AmenityCategory.MEDICAL_CARE]: {
    en: "Medical & Care Support",
    ml: "മെഡിക്കൽ പരിചരണം",
  },
  [AmenityCategory.WELLNESS_RECOVERY]: {
    en: "Postnatal Wellness & Therapy",
    ml: "ആരോഗ്യവും സുഖപ്രാപ്തിയും",
  },
  [AmenityCategory.FOOD_NUTRITION]: {
    en: "Food & Nutrition",
    ml: "ഭക്ഷണവും പോഷകാഹാരവും",
  },
  [AmenityCategory.GENERAL_SERVICES]: {
    en: "General Amenities",
    ml: "പൊതു സൗകര്യങ്ങൾ",
  },
  [AmenityCategory.OTHER]: { en: "Other Facilities", ml: "മറ്റ് സൗകര്യങ്ങൾ" },
};

export interface AmenityMaster {
  id: string;
  name: string;
  nameMalayalam?: string;
  category?: AmenityCategory;
  iconKey: string;
  isActive: boolean;
  sortOrder?: number;
}

export interface CentrePhoto {
  id: string;
  centreId: string;
  url: string;
  sortOrder?: number;
  isCover: boolean;
  uploadedAt: Date;
}

export interface RoomPhoto {
  id: string;
  roomTypeId: string;
  url: string;
  isCover: boolean;
  sortOrder: number;
  uploadedAt: Date | string;
}

export interface RoomType {
  id: string;
  centreId: string;
  name: string;
  category: RoomCategory;
  layoutType?: RoomLayoutType | null;
  occupancy?: string | null;
  style: RoomStyle;
  bedCount: number;
  bedroomCount?: number;
  bathroomCount?: number;
  hasLivingRoom?: boolean;
  hasKitchen?: boolean;
  hasSharedKitchen?: boolean;
  hasCommonBathroom?: boolean;
  hasDiningArea?: boolean;
  hasBalcony?: boolean;
  hasMassageRoom?: boolean;
  sqftArea?: number | null;
  pricePerNight: number;
  description?: string;
  includedAmenities: string[];
  isActive: boolean;
  createdAt: Date;
  photos?: RoomPhoto[];
}

export interface Package {
  id: string;
  centreId: string;
  name: string;
  durationNights: number;
  careType: CareType;
  basePrice: number;
  description?: string;
  includedItems?: any;
  isActive: boolean;
  createdAt: Date;
}

export interface AddonService {
  id: string;
  centreId: string;
  name: string;
  pricePerDay: number;
  description?: string;
  isActive: boolean;
}

export interface AvailabilityBlock {
  id: string;
  centreId: string;
  roomTypeId?: string;
  blockedDate: Date;
  reason?: string;
  blockedBy?: string;
  createdAt: Date;
}

export interface Booking {
  id: string;
  bookingRef: string;
  customerId: string;
  centreId: string;
  roomTypeId: string;
  packageId?: string;
  careType: CareType;
  checkinDate: Date;
  checkoutDate: Date;
  durationNights: number;
  guestCount?: number;
  specialRequests?: string;
  status: BookingStatus;
  rejectionReason?: string;
  internalNotes?: string;
  acceptedAt?: Date;
  acceptedBy?: string;
  checkedinAt?: Date;
  checkedoutAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface BookingAddon {
  id: string;
  bookingId: string;
  addonServiceId: string;
  pricePerDay: number;
  totalPrice: number;
}

export interface Payment {
  id: string;
  bookingId: string;
  razorpayOrderId?: string;
  razorpayPaymentId?: string;
  amountRoom: number;
  amountAddons: number;
  amountGst: number;
  amountTotal: number;
  paymentType?: PaymentType;
  paymentMethod?: string;
  status: PaymentStatus;
  refundAmount?: number;
  refundId?: string;
  refundReason?: string;
  paidAt?: Date;
  createdAt: Date;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  priceMonthly: number;
  maxCentres: number;
  maxRooms?: number;
  features?: any;
  isActive: boolean;
  sortOrder?: number;
  createdAt: Date;
}

export interface ProviderSubscription {
  id: string;
  providerId: string;
  planId: string;
  razorpaySubId?: string;
  status: SubscriptionStatus;
  startedAt?: Date;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  gracePeriodEnd?: Date;
  cancelledAt?: Date;
  cancelReason?: string;
  createdAt: Date;
}

export interface SubscriptionPayment {
  id: string;
  subscriptionId: string;
  providerId: string;
  razorpayPaymentId?: string;
  amount: number;
  periodStart: Date;
  periodEnd: Date;
  status: PaymentStatus;
  paidAt?: Date;
  createdAt: Date;
}

export interface Review {
  id: string;
  bookingId: string;
  customerId: string;
  centreId: string;
  ratingOverall: number;
  ratingCleanliness?: number;
  ratingFood?: number;
  ratingStaff?: number;
  ratingFacilities?: number;
  reviewText?: string;
  photos: string[];
  status: ReviewStatus;
  flaggedReason?: string;
  moderatedBy?: string;
  moderatedAt?: Date;
  createdAt: Date;
}

export interface ChatConversation {
  id: string;
  customerId: string;
  centreId: string;
  bookingId?: string;
  lastMessageAt?: Date;
  customerUnread?: number;
  providerUnread?: number;
  createdAt: Date;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderType: SenderType;
  senderId: string;
  message?: string;
  attachmentUrl?: string;
  isRead?: boolean;
  sentAt: Date;
}

export interface Notification {
  id: string;
  recipientType: string;
  recipientId: string;
  type: string;
  title: string;
  body: string;
  data?: any;
  channel: NotificationChannel;
  isRead?: boolean;
  sentAt: Date;
}

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  passwordHash: string;
  role: AdminRole;
  permissions?: any;
  isActive: boolean;
  lastLoginAt?: Date;
  createdAt: Date;
}

export interface AdminActivityLog {
  id: string;
  adminId: string;
  action: string;
  entityType?: string;
  entityId?: string;
  notes?: string;
  ipAddress?: string;
  createdAt: Date;
}

// ============================================
// API HELPERS
// ============================================

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Validation Constants and Helpers
export * from "./validation-constants.js";

// ============================================
// BLOG & KNOWLEDGE HUB MODELS
// ============================================

export interface Category {
  id: string;
  slug: string;
  nameEn: string;
  nameMl: string;
  descriptionEn?: string;
  descriptionMl?: string;
  icon?: string;
  articleCount?: number;
}

export interface Tag {
  id: string;
  slug: string;
  name: string;
}

export interface Author {
  id: string;
  slug: string;
  name: string;
  title: string;
  avatar: string;
  bio: string;
}

export interface MedicalReviewer {
  id: string;
  slug: string;
  name: string;
  title: string;
  medicalRegistrationNo: string;
  hospitalAffiliation: string;
  avatar: string;
  bio: string;
}

export interface ArticleFAQ {
  id: string;
  articleId: string;
  question: string;
  answer: string;
}

export interface CentreRecommendation {
  id: string;
  articleId: string;
  centreId: string;
  relevanceScore: number;
  calloutTextEn?: string;
  calloutTextMl?: string;
}

export interface Article {
  id: string;
  slug: string;
  titleEn: string;
  titleMl?: string;
  summaryEn: string;
  summaryMl?: string;
  contentEn: string;
  contentMl?: string;
  heroImage: string;
  heroImageAlt: string;
  readingTimeMinutes: number;
  isFeatured: boolean;
  isTrending: boolean;
  viewsCount: number;
  status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
  publishedAt: string;
  updatedAt: string;
  categoryId: string;
  category?: Category;
  authorId?: string;
  author?: Author;
  medicalReviewerId?: string;
  medicalReviewer?: MedicalReviewer;
  tags?: Tag[];
  faqs?: ArticleFAQ[];
  recommendedCentres?: CentreRecommendation[];
}

// ============================================
// MEDICINE, PREMIUM SERVICES, SPECIALIST TYPES
// ============================================

export enum SpecialistRole {
  DOCTOR = "DOCTOR",
  THERAPIST = "THERAPIST",
}

export enum VisitFrequency {
  DAILY = "DAILY",
  WEEKLY = "WEEKLY",
  ON_CALL = "ON_CALL",
}

export interface Medicine {
  id: string;
  nameEn: string;
  nameMl: string;
  descriptionEn?: string;
  descriptionMl?: string;
  category?: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface BusinessMedicine {
  id: string;
  businessId: string;
  medicineId: string;
  dosageInstructionsEn?: string;
  dosageInstructionsMl?: string;
  frequencyEn?: string;
  frequencyMl?: string;
  isPaid: boolean;
  price?: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  medicine?: Medicine;
}

export interface PremiumService {
  id: string;
  businessId: string;
  nameEn: string;
  nameMl: string;
  descriptionEn?: string;
  descriptionMl?: string;
  price?: number;
  isHighlighted: boolean;
  isActive: boolean;
  imageUrl?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Specialist {
  id: string;
  businessId: string;
  name: string;
  role: SpecialistRole;
  specialtyEn: string;
  specialtyMl: string;
  bioEn?: string;
  bioMl?: string;
  photoUrl?: string;
  visitFrequency: VisitFrequency;
  visitScheduleNotesEn?: string;
  visitScheduleNotesMl?: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export enum LeadType {
  CENTRE_VISIT = "CENTRE_VISIT",
  ROOM_INTEREST = "ROOM_INTEREST",
}

export enum LeadStatus {
  NEW = "NEW",
  CONTACTED = "CONTACTED",
  INTERESTED = "INTERESTED",
  CONVERTED = "CONVERTED",
  LOST = "LOST",
}

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  [LeadStatus.NEW]: "New",
  [LeadStatus.CONTACTED]: "Contacted",
  [LeadStatus.INTERESTED]: "Interested",
  [LeadStatus.CONVERTED]: "Converted",
  [LeadStatus.LOST]: "Lost",
};

export interface LeadInteraction {
  id: string;
  type: LeadType;
  roomId?: string;
  roomName?: string;
  timestamp: string;
}

export interface LeadItem {
  id: string;
  businessId: string;
  centreId: string;
  centreName: string;
  customerId: string;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
  leadType: LeadType;
  status: LeadStatus;
  notes?: string;
  lastViewedRoomId?: string;
  lastViewedRoomName?: string;
  visitCount: number;
  roomViewCount: number;
  totalInteractionCount: number;
  interactions: LeadInteraction[];
  createdAt: string;
  updatedAt: string;
}

export interface TrackLeadDto {
  businessId: string;
  leadType: LeadType;
  roomId?: string;
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
}

export interface UpdateLeadStatusDto {
  status: LeadStatus;
  notes?: string;
}
